import FullLayout from 'layout/FullLayout';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconBrandInstagram, IconBrandWhatsapp } from '@tabler/icons-react';
import { exhibitions } from './default';
import Skeleton from 'react-loading-skeleton';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

function ImgSkel({
  src,
  alt,
  wrapperClass = 'relative overflow-hidden',
  imgClass = 'h-full w-full object-cover',
  fallback = '/placeholder.jpg',
}) {
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(false);

  return (
    <div className={wrapperClass}>
      {!loaded && (
        <Skeleton className="absolute inset-0 h-full w-full" containerClassName="h-full w-full" />
      )}
      <img
        src={normalizeImageSrc(err ? fallback : src)}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setErr(true);
          setLoaded(true);
        }}
        className={`${imgClass} transition-all duration-700 ease-in-out ${
          loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
        }`}
        loading="lazy"
      />
    </div>
  );
}

function extractArtworkSize(size = '', info = '') {
  if (size) return size;
  const matched = info.match(
    /(Ø\s*\d+(?:[.,]\d+)?\s*cm|\d+(?:[.,]\d+)?\s*x\s*\d+(?:[.,]\d+)?(?:\s*x\s*\d+(?:[.,]\d+)?)?\s*cm)/i,
  );
  return matched ? matched[0] : '';
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeRegex(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatDescHtml(desc = '', artistName = '') {
  const safeDesc = escapeHtml(desc).replace(/\n/g, '<br/>');
  if (!artistName) return safeDesc;

  const safeName = escapeHtml(artistName);
  const pattern = new RegExp(`(${escapeRegex(safeName)})`, 'i');
  return safeDesc.replace(pattern, '<strong>$1</strong>');
}

function createMasonryColumns(items = [], columnCount = 1) {
  const columns = Array.from({ length: columnCount }, () => []);
  items.forEach((item, index) => {
    columns[index % columnCount].push(item);
  });
  return columns;
}

const ExhibitionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState('id');
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  const findExhibitionById = (targetId) => {
    if (!targetId) return null;
    const targetStringId = String(targetId);

    const exactMatch = exhibitions.find((ex) => String(ex.id) === targetStringId);
    if (exactMatch) return exactMatch;

    const numericTarget = Number(targetStringId);
    if (Number.isNaN(numericTarget)) return null;

    return exhibitions.find((ex) => Number(ex.id) === numericTarget) || null;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  const exhibition = findExhibitionById(id);
  const galleryOrderedExhibitions = [...exhibitions].reverse();
  const galleryActiveIndex = galleryOrderedExhibitions.findIndex(
    (ex) => exhibition && ex.id === exhibition.id,
  );
  const newerExhibition =
    galleryActiveIndex > 0 ? galleryOrderedExhibitions[galleryActiveIndex - 1] : null;
  const olderExhibition =
    galleryActiveIndex >= 0 && galleryActiveIndex < galleryOrderedExhibitions.length - 1
      ? galleryOrderedExhibitions[galleryActiveIndex + 1]
      : null;

  const t = {
    id: {
      backToGallery: 'Kembali ke Galeri',
      artistsCount: 'Seniman',
      artistsWorks: 'Para Seniman & Karyanya',
      followArtist: 'Ikuti Seniman',
      askArtwork: 'Tanya Karya via WhatsApp',
      openWhatsapp: 'Buka WhatsApp',
      previous: 'Sebelumnya',
      next: 'Selanjutnya',
      newer: 'Lebih Baru',
      older: 'Lebih Lama',
      back: 'Kembali',
      backToAbout: 'Kembali ke About',
    },
    en: {
      backToGallery: 'Back to Gallery',
      artistsCount: 'Artists',
      artistsWorks: 'The Artists & Their Works',
      followArtist: 'Follow Artist',
      askArtwork: 'Ask via WhatsApp',
      openWhatsapp: 'Open WhatsApp',
      previous: 'Previous',
      next: 'Next',
      newer: 'Newer',
      older: 'Older',
      back: 'Back',
      backToAbout: 'Back to About',
    },
  };

  const getLocalizedText = (localizedValue, fallback = '') => {
    if (localizedValue && typeof localizedValue === 'object') {
      return localizedValue[language] || localizedValue.en || localizedValue.id || fallback;
    }
    return fallback;
  };

  if (!exhibition) {
    return (
      <FullLayout>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <h2 className="text-2xl font-bold text-center text-gray-800">Exhibition Not Found</h2>
        </div>
      </FullLayout>
    );
  }

  const accentColor = exhibition.poster_color || '#F58136';
  const localizedLabel = getLocalizedText(exhibition.locale?.label, exhibition.label);
  const localizedSublabel = getLocalizedText(exhibition.locale?.sublabel, exhibition.sublabel);
  const localizedSubtitle = getLocalizedText(exhibition.locale?.subtitle, exhibition.subtitle);
  const localizedDescription = getLocalizedText(exhibition.locale?.desc, exhibition.desc);

  const waNumber = '6281290904616';
  const wa = (text) => `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;

  const goToAbout = (targetExhibitionId) => {
    const normalizedExhibitionId =
      targetExhibitionId && typeof targetExhibitionId === 'object' ? id : targetExhibitionId || id;

    const params = new URLSearchParams({
      section: 'kinartgallery',
      exhibitionId: String(normalizedExhibitionId),
    });
    navigate(`/about?${params.toString()}`);
  };

  const getArtworkInquiryText = (artist, art) => {
    const artworkName = getLocalizedText(art.locale?.name, art.name);
    const artworkInfo = getLocalizedText(art.locale?.info, art.info);
    const artworkSize = extractArtworkSize(art.size, artworkInfo);

    if (language === 'id') {
      return `Halo KiN Space, saya tertarik dengan karya "${artworkName}" dari ${
        artist.name
      } pada pameran ${localizedLabel}. Boleh minta informasi lebih lanjut${
        artworkSize ? ` (ukuran: ${artworkSize})` : ''
      }?`;
    }

    return `Hi KiN Space, I'm interested in the artwork "${artworkName}" by ${
      artist.name
    } from the ${localizedLabel} exhibition. Could I get more details${
      artworkSize ? ` (size: ${artworkSize})` : ''
    }?`;
  };

  const renderArtworkCard = (artist, artistIdx, art, artIdx) => (
    <article
      key={artIdx}
      role="button"
      tabIndex={0}
      onClick={() => setSelectedArtwork(`${artistIdx}-${artIdx}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setSelectedArtwork(`${artistIdx}-${artIdx}`);
        }
      }}
      className={`block break-inside-avoid group relative overflow-hidden rounded-xl border bg-white pb-4 transition-all hover:shadow-lg ${
        selectedArtwork === `${artistIdx}-${artIdx}`
          ? 'border-primary-main shadow-md ring-2 ring-primary-main/20'
          : 'border-gray-100'
      }`}>
      {selectedArtwork === `${artistIdx}-${artIdx}` && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            window.open(wa(getArtworkInquiryText(artist, art)), '_blank');
          }}
          aria-label={t[language].openWhatsapp}
          title={t[language].openWhatsapp}
          className="absolute right-4 bottom-4 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105">
          <IconBrandWhatsapp className="h-4 w-4" />
        </button>
      )}

      <div className="overflow-hidden rounded-t-lg">
        <ImgSkel
          src={art.image}
          alt={art.name}
          wrapperClass="w-full bg-gray-50"
          imgClass="h-auto w-full transition-transform duration-700 group-hover:scale-110"
        />
      </div>
      <div className="mt-4 space-y-1 px-4">
        <h4 className="text-sm font-bold text-gray-900 leading-tight">
          {getLocalizedText(art.locale?.name, art.name)}
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold text-primary-main uppercase">
            {extractArtworkSize(art.size, getLocalizedText(art.locale?.info, art.info))}
          </span>
          <span className="h-1 w-1 rounded-full bg-gray-300" />
          <span className="text-[11px] text-gray-500 italic">
            {getLocalizedText(art.locale?.info, art.info)}
          </span>
        </div>
      </div>
    </article>
  );

  return (
    <FullLayout>
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
        <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4 lg:mb-8">
          <button
            onClick={() => goToAbout()}
            className="group inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-kin-text transition-colors hover:text-primary-main">
            <span className="transition-transform group-hover:-translate-x-1">←</span>
            {t[language].backToGallery}
          </button>

          <div className="flex items-center gap-3 self-start sm:self-auto">
            <div className="inline-flex overflow-hidden rounded-full border border-gray-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setLanguage('id')}
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  language === 'id'
                    ? 'bg-primary-main text-white'
                    : 'text-kin-text hover:bg-gray-100'
                }`}>
                ID
              </button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  language === 'en'
                    ? 'bg-primary-main text-white'
                    : 'text-kin-text hover:bg-gray-100'
                }`}>
                EN
              </button>
            </div>
          </div>
        </div>

        <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm sm:rounded-3xl">
          {/* Main Display Area */}
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="relative lg:col-span-5">
              <div className="relative overflow-hidden rounded-tl-2xl rounded-br-2xl border border-gray-100 bg-white">
                <ImgSkel
                  src={exhibition.poster_image}
                  alt={localizedLabel}
                  wrapperClass="w-full overflow-hidden bg-white"
                  imgClass="h-full w-full object-contain"
                />

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                <div className="absolute inset-x-0 bottom-0 p-3 sm:p-5">
                  {/* Floating Stack Artist */}
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2 sm:bottom-6 sm:left-6 sm:right-6 sm:items-center sm:gap-4">
                    <div className="flex -space-x-2 items-center sm:-space-x-3">
                      {exhibition.collaborators.map((artist, idx) => (
                        <ImgSkel
                          key={idx}
                          src={artist.avatar}
                          alt={artist.name}
                          wrapperClass="inline-block h-10 w-10 rounded-full ring-2 ring-white transition-transform hover:z-10 hover:-translate-y-2 sm:h-14 sm:w-14"
                          imgClass="h-full w-full object-contain rounded-full object-cover"
                        />
                      ))}
                      <div className="flex h-7 items-center justify-center rounded-full bg-white/20 px-2 text-[10px] font-normal text-white backdrop-blur-md sm:h-8 sm:px-3 sm:text-xs">
                        +{exhibition.collaborators.length} Artists
                      </div>
                    </div>

                    <div className="max-w-[45%] truncate rounded-full bg-white/10 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur-md sm:max-w-none sm:px-3 sm:text-[10px] sm:tracking-widest">
                      {localizedSubtitle}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 p-4 sm:p-8 lg:p-10">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold uppercase tracking-tight text-kin-textdark sm:text-3xl lg:text-2xl">
                    {localizedLabel}
                  </h1>
                  {localizedSublabel && (
                    <p className="mt-1 font-serif text-base italic text-kin-text sm:text-lg lg:text-xl">
                      {localizedSublabel}
                    </p>
                  )}
                </div>

                <div className="inline-flex w-max flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-[8px] font-bold uppercase tracking-[0.13em] text-kin-text sm:w-auto sm:rounded-full sm:gap-3 sm:px-4 sm:text-[10px] sm:tracking-widest">
                  <span
                    className="inline-flex h-2 w-2 rounded-full"
                    style={{ backgroundColor: accentColor }}
                  />
                  <span>{localizedSubtitle}</span>
                  <span className="text-gray-300">•</span>
                  <span>
                    {exhibition.collaborators.length} {t[language].artistsCount}
                  </span>
                </div>
              </div>

              <div
                className="mt-6 h-1 w-24 rounded-full"
                style={{ backgroundColor: accentColor }}
              />

              <div className="mt-5 space-y-2 sm:mt-6">
                {localizedDescription
                  .split(/\n+/)
                  .filter((text) => text.trim().length)
                  .map((text, idx) => (
                    <p
                      key={idx}
                      className="text-xs leading-relaxed text-kin-textdark sm:text-sm lg:text-base">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: text.trim(),
                        }}
                      />
                    </p>
                  ))}
              </div>
            </div>
          </div>

          <div className="bg-white px-4 py-12 sm:px-8 sm:py-16 lg:px-10 lg:py-20">
            <div className="mb-10 flex flex-col items-center text-center sm:mb-16">
              <h2 className="text-[10px] font-black uppercase tracking-[0.5em] text-gray-400">
                {t[language].artistsWorks}
              </h2>
              <div className="mt-4 h-px w-40 bg-gray-200" />
            </div>

            <div className="space-y-16 sm:space-y-24 lg:space-y-32">
              {exhibition.collaborators.map((artist, idx) => (
                <section key={idx} className="relative">
                  {/* Bio Seniman - Tampilan Lebih Editorial */}
                  <div className="mb-8 grid grid-cols-1 gap-6 sm:mb-10 sm:gap-8 lg:mb-12 lg:grid-cols-12 lg:items-start">
                    <div className="mx-auto w-full max-w-[280px] lg:col-span-4 lg:mx-0 lg:max-w-none">
                      <div className="relative aspect-square overflow-hidden rounded-2xl shadow-xl">
                        <ImgSkel
                          src={artist.avatar}
                          alt={artist.name}
                          imgClass="h-full w-full object-cover"
                        />
                      </div>
                    </div>

                    <div className="lg:col-span-8">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-main sm:text-xs sm:tracking-[0.2em]">
                        {getLocalizedText(artist.locale?.role, artist.role)}
                      </span>
                      <h3 className="mt-2 font-serif text-3xl text-kin-textdark sm:text-4xl lg:text-5xl">
                        {artist.name}
                      </h3>
                      <p className="mt-4 max-w-2xl text-sm leading-relaxed text-kin-textdark sm:mt-6 sm:text-base">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: artist.desc,
                          }}
                        />
                      </p>
                      <a
                        href={artist.href}
                        className="mt-6 inline-flex items-center gap-2 border-b-2 border-primary-main pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-kin-textdark transition-colors hover:text-primary-main sm:mt-8 sm:text-xs sm:tracking-widest">
                        {t[language].followArtist}
                        <IconBrandInstagram className="h-4 w-4" stroke={2.2} />
                      </a>
                    </div>
                  </div>

                  {/* Artworks - Masonry Left Flow */}
                  {(() => {
                    const artworksWithIndex = artist.artworks.map((art, artIdx) => ({
                      art,
                      artIdx,
                    }));
                    const twoColumns = createMasonryColumns(artworksWithIndex, 2);
                    const threeColumns = createMasonryColumns(artworksWithIndex, 3);

                    return (
                      <>
                        <div className="space-y-6 sm:hidden">
                          {artworksWithIndex.map(({ art, artIdx }) =>
                            renderArtworkCard(artist, idx, art, artIdx),
                          )}
                        </div>

                        <div className="hidden gap-6 sm:grid sm:grid-cols-2 lg:hidden">
                          {twoColumns.map((column, colIdx) => (
                            <div key={colIdx} className="space-y-6">
                              {column.map(({ art, artIdx }) =>
                                renderArtworkCard(artist, idx, art, artIdx),
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="hidden gap-6 lg:grid lg:grid-cols-3">
                          {threeColumns.map((column, colIdx) => (
                            <div key={colIdx} className="space-y-6">
                              {column.map(({ art, artIdx }) =>
                                renderArtworkCard(artist, idx, art, artIdx),
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </section>
              ))}
            </div>
          </div>
        </article>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:mt-6 sm:grid-cols-2">
          <button
            onClick={() =>
              newerExhibition ? navigate(`/about/exhibition/${newerExhibition.id}`) : goToAbout()
            }
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-left transition-colors hover:border-gray-300">
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-gray-400">
              {t[language].newer}
            </p>
            <p className="mt-1 text-base font-bold uppercase text-kin-textdark">
              {getLocalizedText(newerExhibition?.locale?.label, newerExhibition?.label || '') ||
                t[language].backToAbout}
            </p>
          </button>

          <button
            onClick={() =>
              olderExhibition ? navigate(`/about/exhibition/${olderExhibition.id}`) : goToAbout()
            }
            className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-left transition-colors hover:border-gray-300">
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-gray-400">
              {t[language].older}
            </p>
            <p className="mt-1 text-base font-bold uppercase text-kin-textdark">
              {getLocalizedText(olderExhibition?.locale?.label, olderExhibition?.label || '') ||
                t[language].backToAbout}
            </p>
          </button>
        </div>
      </section>
    </FullLayout>
  );
};

export default ExhibitionDetail;
